import { uniqBy } from 'lodash';
import { AxiosError } from 'axios';
import { v4 as generateUuid } from 'uuid';
import { InvoiceDto, OrderResponseDto, PagedListShipToPartyDto } from '@atlassian/commerce-ccp-gateway-client';
import { Maybe } from '@atlassian/cssxp-typescommon';
import { createAxiosClient, ServiceProxyMap } from '@atlassian/temp-client-utils';
import { QuoteMetaData } from '@css-xp/graphtypes/src/generated-types-sxp';
import { PartnerSearchOptions, PartnerSearchContext } from '@css-xp/ppc-graphtypes';
import {
  buildMustQuery,
  buildSearchQuery,
  buildShouldQuery,
} from '@css-xp/ppcserver/src/services/ship-to-party-service-helper';

const SHIP_TO_PARTY_CLASS = 'shipToParty';
const client = createAxiosClient({ baseURL: '' }, ServiceProxyMap.CCP);

interface CCPVaidationErrorItem {
  itemId: string;
  errors: Array<{ message: string }>;
}
export class CCPVaidationError extends Error {
  title: string;
  items: CCPVaidationErrorItem[];
  code: string;

  constructor(title: string, items: CCPVaidationErrorItem[] = [], code = '') {
    super(title);
    this.title = title;
    this.items = items;
    this.code = code;
  }
}

interface BulkAssignRoleResourcePayload {
  assignments: Array<{
    role: string;
    resourceAri: string;
    userId: string;
  }>;
}

interface PaypalPaymentMethodInput {
  currency: string;
  type?: 'DEFERRED' | 'PAYPAL';
  payPal: {
    name?: string;
    merchantId?: string;
    email: string;
    nonce: string;
  };
}

interface CreatePaypalPaymentMethodRequest {
  transactionAccountId: string;
  requestPayload: PaypalPaymentMethodInput;
}

export const bulkAssignRoleResourceToPrincipal = async (
  requestPayload: BulkAssignRoleResourcePayload,
  transactionAccountId: string
): Promise<unknown> => {
  const response = await client.post('/api/v1/roles/assignments/create/bulk', requestPayload, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });
  return response.data;
};

interface DeleteAssignedPrincipalToRoleResourcePayload {
  resourceAri: string;
  role: string;
  userId: string;
}

export const deleteAssignedPrincipalToRoleResource = async (
  requestPayload: DeleteAssignedPrincipalToRoleResourcePayload
): Promise<unknown> => {
  const response = await client.post('/api/v1/roles/assignments/delete', requestPayload);
  return response.data;
};

export const getInvoiceDataById = async (invoiceId: string, transactionAccountId: string): Promise<InvoiceDto> => {
  const response = await client.get(`/api/v1/invoices/${invoiceId}`, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });
  return response.data;
};

export const getOrderDetailsById = async (orderId: string, transactionAccountId: string): Promise<OrderResponseDto> => {
  const response = await client.get(`/api/v1/orders/${orderId}`, {
    headers: {
      'Content-Type': 'application/json',
      'X-transaction-account': transactionAccountId,
    },
  });
  return response.data;
};

interface QuoteOrderRequestPayload {
  source: string;
  orderId: string;
  quote: {
    id: string;
    version: Maybe<number> | undefined;
  };
  metadata?: Maybe<QuoteMetaData> | undefined;
}

const getQuoteVersion = async (quoteId: string, transactionAccountId: string) => {
  const response = await client.get(`api/v1/quotes/${quoteId}`, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });

  return response.data.version;
};
export const placeQuoteOrder = async (requestPayload: QuoteOrderRequestPayload, transactionAccountId: string) => {
  requestPayload.quote.version = await getQuoteVersion(requestPayload.quote.id, transactionAccountId);
  try {
    const response = await client.post('/api/v1/orders', requestPayload, {
      headers: {
        'X-transaction-account': transactionAccountId,
      },
    });
    return response.data;
  } catch (error) {
    // handling CCP validation errors
    if (error instanceof AxiosError && error?.response?.data) {
      const { title, items } = error.response.data;
      throw new CCPVaidationError(title, items);
    }
    throw error;
  }
};

const getQuoteDetails = async (quoteId: string, transactionAccountId: string) => {
  const response = await client.get(`api/v2/quotes/${quoteId}`, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });

  return response.data;
};

const getEntitlementDetails = async (entitlementId: string) => {
  const response = await client.get(`api/v1/entitlements/${entitlementId}/details`);
  return response.data;
};

const getRelatedEntitlementDetails = async (lineItem: any) => {
  //lineItem entitlementId will always be there as we are calling this only for amend line items
  const payload = {
    entitlementId: lineItem.entitlementId,
    entitlements: [
      {
        traverse: {
          relates: '*',
          break: {
            depth: '*',
          },
        },
      },
    ],
  };

  try {
    // fetching entitlementdetails for transaction account, for transfer case txa can be different
    const entitlementDetails = await getEntitlementDetails(lineItem.entitlementId);

    //calling traverse API to fetch all related entitlements
    const response = await client.post(`api/v1/entitlements/relationships/traverse`, payload, {
      headers: {
        'X-transaction-account': entitlementDetails?.transactionAccountId,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error?.response?.data) {
      const { title, items } = error.response.data;
      throw new CCPVaidationError(title, items);
    }
    throw error;
  }
};

const getPayloadLineItems = async (lineItems: any, invoiceGroupId: string) => {
  lineItems = lineItems.map((item: any) => ({
    ...item,
    type: item.lineItemType,
  }));

  //Extract AMEND_ENTITLEMENT line Items
  const amendLineItems = lineItems.filter((item: any) => item.lineItemType === 'AMEND_ENTITLEMENT');

  //Extract CREATE_ENTITLEMENT line Items
  const createLineItems = lineItems.filter((item: any) => item.lineItemType === 'CREATE_ENTITLEMENT');

  //fetching entitlementDetails for all amend lineItems
  const amendLineItemsIds = amendLineItems.map((item: any) => item.entitlementId);

  //fetching entitlementDetails for all relatesFromEntitlements
  const relatesFromEntitlementDetails = await Promise.all(amendLineItems.map(getRelatedEntitlementDetails));

  const relatesFromEntitlementIds = relatesFromEntitlementDetails
    .map((item: any) => item.entitlements?.map((entitlement: any) => entitlement.entitlementId))
    .reduce((a: string, b: string) => a.concat(b), []);

  const allEntitlementIdArray = [...relatesFromEntitlementIds, ...amendLineItemsIds];
  const uniqueEntitlementIdArray = uniqBy(allEntitlementIdArray, function (id: string) {
    return id;
  });
  const entitlementDetails = await Promise.all(uniqueEntitlementIdArray.map(getEntitlementDetails));

  //send account modification only if invoice group is changed
  const accountModificationEntitlementPayload = entitlementDetails
    .filter((entitlement) => entitlement.invoiceGroupId !== invoiceGroupId)
    .map((entitlement) => ({
      lineItemId: generateUuid(),
      entitlementId: entitlement.entitlementId,
      entitlementVersion: entitlement.version,
      type: 'ACCOUNT_MODIFICATION',
    }));

  const payloadLineItems = [...amendLineItems, ...createLineItems, ...accountModificationEntitlementPayload];
  return payloadLineItems;
};
export const placeQuoteOrderV2Deprecated = async (
  requestPayload: QuoteOrderRequestPayload,
  transactionAccountId: string,
  invoiceGroupId: string,
  isBillingProfileChanged: boolean
) => {
  let isOrderEnabled = true;
  if (isBillingProfileChanged) {
    isOrderEnabled = false;

    try {
      const quoteDetails = await getQuoteDetails(requestPayload.quote.id, transactionAccountId);
      const lineItems = quoteDetails.lineItems;

      const payloadLineItems = await getPayloadLineItems(lineItems, invoiceGroupId);

      const requestPatchPayload = {
        invoiceGroupId: invoiceGroupId,
        lineItems: payloadLineItems,
        version: quoteDetails.version,
      };

      try {
        await client.patch(`/api/v2/quotes/${requestPayload.quote.id}`, requestPatchPayload, {
          headers: {
            'X-transaction-account': transactionAccountId,
          },
        });
        isOrderEnabled = true;
      } catch (error) {
        if (error instanceof AxiosError && error?.response?.data) {
          const { title, items } = error.response.data;
          throw new CCPVaidationError(title, items);
        }
        throw error;
      }
    } catch (error) {
      // handling CCP validation errors
      if (error instanceof AxiosError && error?.response?.data) {
        const { title, items } = error.response.data;
        throw new CCPVaidationError(title, items);
      }
      throw error;
    }
  }

  if (isOrderEnabled) {
    requestPayload.quote.version = await getQuoteVersion(requestPayload.quote.id, transactionAccountId);

    try {
      const response = await client.post('/api/v1/orders', requestPayload, {
        headers: {
          'X-transaction-account': transactionAccountId,
        },
      });
      return response.data;
    } catch (error) {
      // handling CCP validation errors
      if (error instanceof AxiosError && error?.response?.data) {
        const { title, items } = error.response.data;
        throw new CCPVaidationError(title, items);
      }
      throw error;
    }
  }
};

export const getBraintreeClientToken = async () => {
  try {
    const response = await client.get('/api/v1/braintree/client-token');
    return response.data;
  } catch (error) {
    // handling CCP validation errors
    if (error instanceof AxiosError && error?.response?.data) {
      const { title, items } = error.response.data;
      throw new CCPVaidationError(title, items);
    }
    throw error;
  }
};

export const createPaypalPaymentMethod = async ({
  transactionAccountId,
  requestPayload,
}: CreatePaypalPaymentMethodRequest) => {
  try {
    const response = await client.post('/api/v1/payment-methods', requestPayload, {
      headers: {
        'X-transaction-account': transactionAccountId,
      },
    });
    return response.data;
  } catch (error) {
    // handling CCP validation errors
    if (error instanceof AxiosError && error?.response?.data) {
      const { title, items } = error.response.data;
      throw new CCPVaidationError(title, items);
    }
    throw error;
  }
};

export const getEntitlementCountForTransactionAccount = async (transactionAccountId: string) => {
  try {
    const response = await client.post(
      '/api/v1/search/count',
      {
        payload: {
          query: {
            bool: {
              must: [
                {
                  match: {
                    transactionAccountId,
                  },
                },
              ],
            },
          },
        },
        class: 'entitlement',
      },
      {
        headers: {
          'X-transaction-account': transactionAccountId,
        },
      }
    );
    return response.data;
  } catch (error) {
    // handling CCP validation errors
    if (error instanceof AxiosError && error?.response?.data) {
      const { title, items } = error.response.data;
      throw new CCPVaidationError(title, items);
    }
    throw error;
  }
};

export const getShipToPartiesCountForTransactionAccount = async (
  transactionAccountId: string,
  searchOptions?: PartnerSearchOptions,
  isSoldToAddressArchivalEnabled?: boolean
) => {
  const query = searchOptions?.query ?? undefined;
  const searchContext = searchOptions?.searchContext ?? undefined;

  const shouldQueries = buildShouldQuery(query, searchContext as PartnerSearchContext);
  const mustQueries = buildMustQuery(transactionAccountId, [], [], false, isSoldToAddressArchivalEnabled);
  const searchQuery = buildSearchQuery(mustQueries, shouldQueries);

  try {
    const response = await client.post(
      '/api/v1/search/count',
      {
        payload: {
          query: searchQuery,
        },
        class: SHIP_TO_PARTY_CLASS,
      },
      {
        headers: {
          'X-transaction-account': transactionAccountId,
        },
      }
    );
    return response.data;
  } catch (error) {
    // handling CCP validation errors
    if (error instanceof AxiosError && error?.response?.data) {
      const { title, items } = error.response.data;
      throw new CCPVaidationError(title, items);
    }
    throw error;
  }
};

export const getShipToParties = async (
  transactionAccountId: string,
  pageSize = 1000
): Promise<PagedListShipToPartyDto> => {
  const response = await client.get(`/api/v1/ship-to-parties?pageSize=${pageSize}`, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });
  return response.data;
};

interface UpdateInvoiceGroupPayload {
  active?: boolean;
}

export const patchInvoiceGroup = async (
  invoiceGroupId: string,
  transactionAccountId: string,
  payload: UpdateInvoiceGroupPayload
) => {
  const response = await client.patch(`/api/v1/invoice-groups/${invoiceGroupId}`, payload, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });
  return response.data;
};

export const getShipToPartiesCountByCompanyName = async (transactionAccountId: string, companyName: string) => {
  const mustQueries = buildMustQuery(transactionAccountId, [], [companyName]);
  const searchQuery = buildSearchQuery(mustQueries, []);

  try {
    const response = await client.post(
      '/api/v1/search/count',
      {
        payload: {
          query: searchQuery,
        },
        class: SHIP_TO_PARTY_CLASS,
      },
      {
        headers: {
          'X-transaction-account': transactionAccountId,
        },
      }
    );
    return response.data;
  } catch (error) {
    // handling CCP validation errors
    if (error instanceof AxiosError && error?.response?.data) {
      const { title, items } = error.response.data;
      throw new CCPVaidationError(title, items);
    }
    throw error;
  }
};

export const deactivateShipToParty = async (shipToPartyId: string, transactionAccountId: string) => {
  const response = await client.put(`/api/v1/ship-to-parties/${shipToPartyId}/deactivate`, undefined, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });
  return response.data;
};

export const getSetupIntents = async (transactionAccountId: string) => {
  const response = await client.post('/api/v1/setup-intents', undefined, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });
  return response.data;
};

export const getPaymentMethodById = async (paymentMethodId: string, transactionAccountId: string) => {
  const response = await client.get(`/api/v1/payment-methods/${paymentMethodId}`, {
    headers: {
      'X-transaction-account': transactionAccountId,
    },
  });
  return response.data;
};
